import { useState, useEffect, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import HeroSection from "./components/HeroSection";
import CaliforniaCommunitiesWeServe from "./components/CommunitiesWeServe";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const CitiesDirectory = lazy(() => import("./components/CitiesDirectory"));
const StateResources = lazy(() => import("./components/StateResources"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const FinalCTA = lazy(() => import("./components/FinalCTA"));

export default function CaliforniaPage() {
  useEffect(() => {
    document.title =
      "Expedited & Emergency Passport Services California | Same-Day CA Service | Fast Passport Center";

    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Fast Passport Center provides emergency, expedited, and same-day passport services across all California cities. Serving Los Angeles, San Diego, San Francisco, Sacramento, and beyond. Regional agencies in LA, San Francisco, and San Diego."
      );
    }

    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute(
        "content",
        "expedited passport California, emergency passport California, same day passport CA, fast passport Los Angeles, passport renewal San Diego, passport service San Francisco"
      );
    }

    const geoRegion = document.querySelector('meta[name="geo.region"]');
    if (geoRegion) geoRegion.setAttribute("content", "US-CA");
    const geoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (geoPlacename) geoPlacename.setAttribute("content", "California");
    const geoPos = document.querySelector('meta[name="geo.position"]');
    if (geoPos) geoPos.setAttribute("content", "36.7783;-119.4179");
    const icbm = document.querySelector('meta[name="ICBM"]');
    if (icbm) icbm.setAttribute("content", "36.7783, -119.4179");

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical)
      canonical.setAttribute("href", "https://fastpassportcenter.com/california");

    let schema = document.getElementById("ca-schema");
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "ca-schema";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }

    schema.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://fastpassportcenter.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "California",
            item: "https://fastpassportcenter.com/california",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "Fast Passport Center - California",
        description:
          "Expedited and emergency passport services across California — Los Angeles, San Diego, San Francisco, Sacramento, and every city in between.",
        url: "https://fastpassportcenter.com/california",
        telephone: "+1-800-381-3010",
        email: "info@fastpassportcenter.com",
        priceRange: "$$",
        areaServed: {
          "@type": "State",
          name: "California",
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: "08:00",
            closes: "20:00",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Emergency Passport Service California",
        description:
          "Same-day and next-day emergency passport processing for California travelers with urgent international travel needs.",
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: { "@type": "State", name: "California" },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Expedited Passport Renewal California",
        description:
          "2–3 week expedited passport renewal service for California residents.",
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: { "@type": "State", name: "California" },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "New Passport Application California",
        description:
          "First-time passport application assistance with document prep and acceptance facility scheduling for California residents.",
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: { "@type": "State", name: "California" },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Child Passport Service California",
        description:
          "Minor passport applications with parental consent documentation for California families.",
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: { "@type": "State", name: "California" },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Lost or Stolen Passport Replacement California",
        description:
          "Fast replacement of lost, stolen, or damaged passports for California residents.",
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: { "@type": "State", name: "California" },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Name Change Passport Update California",
        description:
          "Passport name change service after marriage, divorce, or legal name change for California residents.",
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: { "@type": "State", name: "California" },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Second Passport California",
        description:
          "Second valid passport applications for California frequent international travelers and business professionals.",
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: { "@type": "State", name: "California" },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Does California have its own passport agency?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "California has three regional passport agencies: Los Angeles (11000 Wilshire Blvd), San Francisco (450 Golden Gate Ave), and San Diego (401 W A Street). All three require appointments and serve travelers with life-or-death emergencies or travel within 14 days.",
            },
          },
          {
            "@type": "Question",
            name: "How fast can I get a passport in California?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "With Fast Passport Center, California residents can get same-day emergency service or 2–3 week expedited processing. Routine service through the post office takes 6–11 weeks plus mailing time.",
            },
          },
          {
            "@type": "Question",
            name: "Do I need to visit an acceptance facility in California?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "First-time applicants and minors must visit an authorized acceptance facility (post office, clerk of court, or library) to have their application sealed. Renewals by mail do not require a facility visit. Fast Passport Center handles all scheduling and document prep.",
            },
          },
          {
            "@type": "Question",
            name: "Which California airport should I fly from with an expired passport?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "LAX, SFO, and SAN are California's busiest international airports. If your passport is expired or expiring soon, contact Fast Passport Center immediately — same-day emergency service is available for travelers with confirmed international flights.",
            },
          },
          {
            "@type": "Question",
            name: "Can I renew my passport by mail in California?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, if your most recent passport was issued within the last 15 years, when you were 16 or older, and is undamaged. Routine renewal by mail takes 6–11 weeks. For faster service, Fast Passport Center offers 2–3 week expedited renewal.",
            },
          },
          {
            "@type": "Question",
            name: "Do I need a passport for a Mexico cruise from San Diego or Long Beach?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Closed-loop cruises (departing and returning to the same U.S. port) may accept a birth certificate and government-issued photo ID. However, a passport is strongly recommended for faster re-entry and in case of emergency disembarkation in Mexico.",
            },
          },
        ],
      },
    ]);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <HeroSection />
      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-gray-50">
            <div className="animate-pulse text-gray-400 text-sm">Loading cities...</div>
          </div>
        }
      >
        <CitiesDirectory />
      </Suspense>
      <CaliforniaCommunitiesWeServe />
      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-white">
            <div className="animate-pulse text-gray-400 text-sm">Loading resources...</div>
          </div>
        }
      >
        <StateResources />
      </Suspense>
      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-gray-50">
            <div className="animate-pulse text-gray-400 text-sm">Loading FAQs...</div>
          </div>
        }
      >
        <FAQSection />
      </Suspense>

      <section className="bg-[#F7F8FA] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Passport Services
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              How We Serve California
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              From the beaches of San Diego to the tech corridors of Silicon Valley, we help
              Californians get their passports fast. Three regional passport agencies in LA, San
              Francisco, and San Diego serve the state — but appointments are hard to get. We cut
              through the red tape.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "ri-flight-takeoff-line",
                title: "Emergency Same-Day Service",
                desc: "For California travelers with flights within 72 hours. We hand-carry your application to a regional agency.",
              },
              {
                icon: "ri-time-line",
                title: "Expedited 2–3 Week Service",
                desc: "Standard expedited processing with overnight shipping and tracking for California residents.",
              },
              {
                icon: "ri-file-list-3-line",
                title: "New Passport Applications",
                desc: "First-time applicants in California — we handle document prep, photos, and acceptance facility scheduling.",
              },
              {
                icon: "ri-refresh-line",
                title: "Passport Renewals",
                desc: "California renewals by mail or courier. We ensure your application is complete to avoid delays.",
              },
              {
                icon: "ri-user-add-line",
                title: "Child & Minor Passports",
                desc: "California families with kids under 16 — we guide you through parental consent and documentation.",
              },
              {
                icon: "ri-error-warning-line",
                title: "Lost, Stolen & Damaged",
                desc: "Fast replacement for California residents who need a new passport quickly.",
              },
            ].map((svc, i) => (
              <div
                key={i}
                className="bg-white rounded-lg p-7 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-5">
                  <i className={`${svc.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{svc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-white">
            <div className="animate-pulse text-gray-400 text-sm">Loading CTA...</div>
          </div>
        }
      >
        <FinalCTA />
      </Suspense>

      <section className="bg-[#F7F8FA] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <SEOCrossLinks state="CA" showCities={false} />
        </div>
      </section>
    </div>
  );
}